
import LoadingOverlay from "./LoadingOverlay.vue";

import SchedulerOverlay from "../overlays/SchedulerOverlay.vue";
import { Vue, Component, Prop, Watch, Emit } from "vue-property-decorator";
import AddionalFilterCard from "../scheduler/AdditionalFilterCard.vue";
import ActivityFormModal from "@/components/projects/activities/ActivityFormModalNew.vue";
import Message from "@/data/notificationsMessages";
import { defaultErrorNotificationMessage } from "@/data/errorMessages";
import { NOTIFICATION } from "@/entities/Events";
import Roles from "@/entities/Roles";
import RangeChangedPayload from "@/entities/scheduler/RangeChangedPayload";
import CustomTimelineGroup from "@/entities/scheduler/CustomTimelineGroup";
import TimelineOptionsWithTemplates from "@/entities/scheduler/TimelineOptionsWithTemplates";
import {
  getEmptyFiltersResultMessage,
  getEmptyScheduleListMessage,
} from "@/helpers/lists/emptyListMessages";

import getFullName from "@/helpers/getFullName";
import getFetchMoreStartDates from "@/helpers/scheduler/getFetchMoreStartDates";

import ProjectDataForModal from "@/entities/modals/ProjectDataForModal";
import GET_CONTRACTORS from "@/apollo/scheduler/GetContractors.gql";
import moment from "moment";
import { cloneDeep } from "lodash-es";
import contractorViewFiltersTemplate from "@/data/filtersTemplates/scheduler/contractorViewFiltersTemplate";
import ContractorViewActivity from "@/entities/scheduler/ContractorViewActivity";
import ActivityDataForModal from "@/entities/modals/ActivityDataForModal";
import ContractorAdvancedFilter from "../scheduler/filters/ContractorAdvancedFilter.vue";
import Scheduler from "../SchedulerV2.vue";
import ProjectModalNew from "@/components/companyProjects/ProjectModalNew.vue";
import { hslColorFromName } from "@/utils/hslColorFromName";
import {
  Company,
  Contractor,
  Contractor_Bool_Exp,
  Project_Activity,
  Project_Activity_Bool_Exp,
  User,
  User_Profile_Bool_Exp,
} from "@/generated/graphql";
import ScheduleInfoIcon from "@/components/icons/ScheduleInfoIcon.vue";
import ensureTimezoneSuffix from "@/utils/ensureTimezoneSuffix";
import { getActivityPercentageComplete } from "@/utils/getActivityPercentage";
import storage from "@/services/storage";
import { APP_COLORS } from "@/data/sidebar";
import { MessageDuration, MessageStatus } from "@/data/notificationsMessages";
import { EVENTS_BORDERS_COLORS, EVENTS_COLORS } from "@/data/schedularColors";
import adjustDates from "@/helpers/adjustTimeForScheduler";

// Add new imports for top filters
import FormGroup from "@/components/form/FormGroupNew.vue";
import UiButtonMessage from "@/components/button/ui-button-message.vue";
import FormDatepicker from "@/components/form/FormDatepickerNew.vue";
import TitlesMultiselect from "@/components/autocompleteMultiselects/ContractorTagsSelector.vue";
import SkillsMultiselect from "@/components/autocompleteMultiselects/SkillsMultiselect.vue";
import ProjectSchedulerEdit from "../ProjectSchedulerEdit.vue";

interface ContractorViewFiltersToWatch {
  countries: string[];
  states: string[];
  cities: string[];
  titles: string[];
  skills: number[];
  minRate: string;
  maxRate: string;
  rangeStart: Date;
  rangeEnd: Date;
  users: string[]
}

@Component({
  components: {
    SchedulerOverlay,
    Scheduler,
    AddionalFilterCard,
    ContractorAdvancedFilter,
    ActivityFormModal,
    ProjectModalNew,
    ScheduleInfoIcon,
    LoadingOverlay,
    ProjectSchedulerEdit,
    // Add new components for top filters
    FormGroup,
    UiButtonMessage,
    FormDatepicker,
    TitlesMultiselect,
    SkillsMultiselect
  },
  apollo: {
    raw_Contractors: {
      query: GET_CONTRACTORS,
      variables() {
        return
      },
      update(data) {
        return data;
      },
      skip: true,
    },
  },
})
export default class ContractorScheduler extends Vue {
  @Prop({ type: Boolean, default: true }) readonly hasData!: boolean;
  @Prop({ type: Boolean, default: true }) readonly isLoading!: boolean;
  @Prop({ type: Array, default: () => [] }) readonly managers!: any[];

  @Prop({ type: Object, required: true }) readonly currentUser!: User;
  @Prop({ type: Object, required: true }) readonly company!: Company;
  @Prop({ type: String, required: true }) readonly companyId!: string;
  @Prop({ type: Date, default: undefined }) readonly axisStart?: Date;
  @Prop({ type: Date, default: undefined }) readonly axisEnd?: Date;
  @Prop({ type: Array, default: () => [] })
  readonly selectedContractorsIds?: string[];
  @Prop({ type: Boolean, default: false })
  readonly showAdditionalFilters!: boolean;
  @Prop({ type: Boolean, default: false })
  readonly shouldClearFilters!: boolean;

  // Add new filter properties
  searchName: string = "";
  searchLocation: string = "";
  searchDate: string = "";

  raw_Contractors = null;
  rawContractors: Contractor[] = [];
  contractors: Contractor[] = [];
  additionalFilters: ContractorViewFiltersToWatch = cloneDeep(
    contractorViewFiltersTemplate
  );
  queryStartTime: Date = moment().startOf("month").subtract(55, "w").toDate();
  queryEndTime: Date = moment().endOf("month").toDate();
  numberOfAppliedFilters = 0;
  // isAdmin = this.currentUser.role.includes()
  aggregateWhere: Contractor_Bool_Exp = { deleted: { _eq: false } }
  modalShown = false;
  modalType = "activity";
  message: string = "You haven't invited any contractors yet";

  projectInfoForModal?: ProjectDataForModal | null = null;
  newlyCreatedItemId?: string = undefined;
  passedStartDate: Date = moment().startOf("month").toDate();
  passedEndDate: Date = moment().endOf("month").toDate();
  isEdittingLocked: boolean = true;

  activityInfoForModal?: ActivityDataForModal | null = null;
  contractorIdForModal?: string = "";

  // Add watcher for search filters
  get searchFiltersToWatch(): string {
    return JSON.stringify([
      this.searchName,
      this.searchLocation,
      this.searchDate
    ]);
  }

  @Watch("searchFiltersToWatch")
  handleSearchFiltersChange() {
    this.rawContractors = [];
    this.contractors = [];
    this.$apollo.queries.raw_Contractors.refetch({
      where: this.where,
      activitiesWhere: { ...this.activitiesWhere, project: {} },
      aggregateWhere: this.aggregateWhere
    });
  }

  // Handle filter change
  handleFilterChange() {
    this.handleSearchFiltersChange();
  }

  // Handle reset button click


  @Emit('update:updateCount')
  updateTotalCount(totalCount: number) {
    return totalCount;
  }

  @Watch('raw_Contractors')
  detectRawContractors() {
    if (this.raw_Contractors?.contractor?.length) {
      this.rawContractors = this.raw_Contractors?.contractor
    }
    this.updateTotalCount(this.raw_Contractors?.total?.aggregate?.count || 0)
  }

  get type() {
    return "Resource";
  }
  get schedularType() {
    return `${this.type}s`;
  }

  created() {
    this.updateTotalCount(0)
    const managers = {
      approved: {
        _eq: true,
      },

    }
    if (!this.isAdmin) {
      this.aggregateWhere = {
        ...this.aggregateWhere, managers: {
          ...managers, manager: {
            user_id: {
              _eq: this.currentUser.id,
            },
          }
        }
      }
    }
    this.$apollo.queries.raw_Contractors.setVariables({
      where: this.where,
      activitiesWhere: { ...this.activitiesWhere, project: {} },
      aggregateWhere: this.aggregateWhere
    });
    this.$apollo.queries.raw_Contractors.skip = false;
  }

  @Watch("propToWatch")
  refetchOnDataFiltersChange(oldVal: string[], newVal: string[]) {
    if (oldVal !== newVal) {
      this.rawContractors = [];
      this.contractors = []
      this.$apollo.queries.raw_Contractors.refetch({
        where: this.where,
        activitiesWhere: { ...this.activitiesWhere, project: {} },
        aggregateWhere: this.aggregateWhere
      });
    }
  }

  @Watch("numberOfAppliedFilters", { immediate: true })
  appliedFiltersChanged() {
    this.$emit("appliedFiltersChanged", this.numberOfAppliedFilters);
  }

  @Watch("rawContractors", { immediate: true })
  rawContractorComposition() {
    if (this.rawContractors.length == 0) {
      return;
    }
    const contractorsAvatarPromises = this.rawContractors.map(
      async (contractor) => {
        if (!contractor.user.avatar) return contractor;
        try {
          const url = await storage.get(contractor.user.avatar);
          return {
            ...contractor,
            profileImage: url,
          };
        } catch (e) {
          return contractor;
        }
      }
    );
    Promise.all(contractorsAvatarPromises)
      .then((contractors) => {
        this.contractors = contractors.map((contractor) => {
          return {
            ...contractor,
          };
        });
      })
      .catch((e) => { });
    // from raw contractors, make avatar url
  }
  get resources() {
    const data = this.contractorWithProjects;

    const processData = data.map((contractor, i) => {

      const children = [];
      let totalTaks = 0;
      contractor.projects.forEach((project) => {
        let activityMaxEndDate: Date = null;
        let activityMinStartDate: Date = null;
        const projectData = {
          id: `${contractor.id}-${project?.id}`,
          type: "project",
          title: project.name,
          name: project.name,
          projectId: project.id,
          group: contractor.id,
          noOftasks: project.activities.length,
          color: project.color,
          children: [],
          entityInfo: {
            project: project,
            activityMaxEndDate,
            activityMinStartDate
          },
        };
        project.activities.forEach((activity, index) => {
          totalTaks += 1
          const percentageComplete = getActivityPercentageComplete(activity);
          const startDate = activity.start_date ? new Date(activity.start_date) : null;
          const endDate = activity.end_date ? new Date(activity.end_date) : null;

          if (activityMaxEndDate === null) {
            activityMaxEndDate = new Date(activity.end_date)
            activityMinStartDate = new Date(activity.start_date)
          }
          else if (startDate && endDate) {
            activityMaxEndDate = activityMaxEndDate.getTime() - endDate.getTime() > 0 ? activityMaxEndDate : endDate;
            activityMinStartDate = activityMinStartDate.getTime() - startDate.getTime() > 0 ? startDate : activityMinStartDate;
          }
          projectData.children.push({
            id: activity.id,
            index: index + 1,
            title: moment(activity.start_date).format('DD/MM/YYYY'),
            name: moment(activity.start_date).format('DD/MM/YYYY'),
            type: "activity",
            start_date: activity.start_date,
            end_date: activity.end_date,
            completed: activity.completed,
            note: activity.note,
            location: activity.location,
            virtual: activity.virtual || false,
            virtual_meeting_link: activity.virtual_meeting_link || null,
            fallback_num: activity.fallback_num || null,
            budget: activity.budget,
            contractor: activity.contractor,
            group: contractor.id,
            confirmed_hours: activity.confirmed_hours,
            color: activity.color,
            progress: percentageComplete,
          });
        });
        // Add the add-activity object
        projectData.entityInfo = {
          ...projectData.entityInfo, activityMaxEndDate,
          activityMinStartDate
        }
        children.push(projectData);
      });
      const projectColor = EVENTS_BORDERS_COLORS[i % EVENTS_BORDERS_COLORS.length];

      return {
        ...contractor,
        title: contractor.name,
        subtitle: contractor.title || '',
        type: "contractor",
        imageUrl: contractor.avatar,
        children,
        noOfTotalTaks: totalTaks,
        borderColor: projectColor,

      };
    });

    return processData;
  }

  get events() {
    const events = this.contractorActivityItems;
    const projects = this.contractorProjectItems;

    return [...events, ...projects];
  }

  get listeners() {
    return {
      cellClick: ({ record, event }) => {
        const expanderClass = 'b-tree-expander'
        const isClassNameString = typeof event.target.className === 'string'
        const isExpander = isClassNameString && event.target?.className?.includes(expanderClass) //The className property on an SVG element has a different definition from the className property of an HTML element.In HTML, className is a string. In SVG it is an SVGAnimatedString object
        // Add a new client when clicking plus icon
        const eventdata = record.eventStore.getById(record.data?.id)

        if (isExpander) return false;
        if (event.target.closest(".b-add-activity")) {
          this.handleCreateBtnClicked(record.data);
        } else if (record.data.type === 'activity') {
          this.handleActivityEventClicked(eventdata.data?.entityInfo);
        }
        else if (record.data.type === 'project') {
          this.handleProjectEventClicked(eventdata.data?.entityInfo);
        }
        return false;
      },
      eventClick: (props) => {
        const { eventRecord } = props;
        //eventRecord.data
        if (eventRecord.data.type === "project") {
          this.handleProjectEventClicked(eventRecord.data.entityInfo);
        }
        if (eventRecord.data.type === "activity") {
          this.handleActivityEventClicked(eventRecord.data.entityInfo);
        }
      },
      beforeEventAdd: () => {
        return false;
      },
      beforeEventResize: ({ eventRecord }) => {
        if (eventRecord.data.type === "contractor") {
          return false;
        }
        return true;
      },
      beforeEventResizeFinalize: ({ source, context }) => {
        context.async = true;

        const { startDate: newStartDate, endDate: newEndDate, eventRecord } = context;
        const { entityInfo: { project, activityMaxEndDate,
          activityMinStartDate, contractorId }, type } = eventRecord.data;
        const sdate = new Date(newStartDate.toISOString()).getTime();
        const edate = new Date(newEndDate.toISOString()).getTime();

        const { id: projectId } = project
        let errorMsg = null;
        if (type === "activity") {
          const uniqeSource = `${contractorId}-${projectId}`;
          const parentSource = source.eventStore.getById(uniqeSource);
          const { startDate, endDate, } = parentSource.data;
          const psdate = new Date(new Date(startDate).toISOString()).getTime()
          const pedate = new Date(new Date(endDate).toISOString()).getTime()

          const isTimeGreaterOrLessthanProject = edate > pedate || sdate < psdate

          if (isTimeGreaterOrLessthanProject) {
            errorMsg = "Activity Start and End Dates must be between Project Start and End Dates."
          }

        }
        if (type === "project") {
          if (activityMaxEndDate && activityMinStartDate) {
            const maxActivityEndTime = new Date(new Date(activityMaxEndDate).toISOString()).getTime()
            const minActivityStartTime = new Date(new Date(activityMinStartDate).toISOString()).getTime()
            const isTimeGreaterOrLessthanActivity = edate < maxActivityEndTime || sdate > minActivityStartTime
            if (isTimeGreaterOrLessthanActivity) {
              errorMsg = "Project Start and End Dates must be greater than activities Start and End Dates."
            }
          }
        }
        if (errorMsg) {
          this.$eventBus.emit(NOTIFICATION, {
            status: MessageStatus.ERROR,
            title: errorMsg,
            duration: MessageDuration.DEFAULT,
            // message: `You rejected hours for ${this.selectedRows.length || 0} resources.`,
          });
          return context.finalize(false);
        }
        context.finalize(true);

      },
      eventResizeEnd: ({ source, eventRecord, changed }) => {
        if (eventRecord.data.type === "contractor" || !changed) {
          return false;
        }
        const { id, resourceId, startDate, endDate, entityInfo: { project, contractorId } } = eventRecord.data;
        const start_date = startDate.toISOString();
        const end_date = endDate.toISOString();
        const newSMilis = new Date(start_date).getTime();
        const newEMilis = new Date(end_date).getTime();
        const { id: projectId } = project
        const activitysource = source.eventStore.getById(resourceId);
        const activityResource = source.resourceStore.getById(resourceId);
        if (eventRecord.data.type === "activity") {
          const uniqeSource = `${contractorId}-${projectId}`;

          const parentEventSource = source.eventStore.getById(uniqeSource);
          const parentReSource = source.resourceStore.getById(uniqeSource);
          const entityInfo = { ...parentEventSource.entityInfo }
          if (entityInfo.activityMaxEndDate && entityInfo.activityMinStartDate) {
            entityInfo.activityMaxEndDate = new Date(new Date(entityInfo.activityMaxEndDate).toISOString()).getTime() < newEMilis ? endDate : entityInfo.activityMaxEndDate
            entityInfo.activityMinStartDate = new Date(new Date(entityInfo.activityMinStartDate).toISOString()).getTime() > newSMilis ? startDate : entityInfo.activityMinStartDate
            parentEventSource.entityInfo = entityInfo
            parentReSource.entityInfo = entityInfo
          }
          activitysource.start = startDate
          activitysource.end = endDate
          activityResource.start_date = startDate
          activityResource.end_date = endDate
          this.$emit("updateBasicActivityData", id, { start_date, end_date });
          return true;
        }
        if (eventRecord.data.type === "project") {
          activityResource.start_date = startDate
          activityResource.end_date = endDate
          activitysource.start = startDate
          activitysource.end = endDate
          this.$emit("updateProject", {
            set: {
              start_date,
              end_date,
            },
            projectId: eventRecord.data.entityInfo.project.id,
            slug: eventRecord.data.entityInfo.project.slug,
            addSkills: [],
            deleteSkillsIds: [],
            customSkills: [],
            updatePersonalSkills: [],
          });
          return true;
        }
        return false;
      },
    };
  }

  get loading(): boolean {
    return (
      this.$apollo.queries.raw_Contractors.loading && !this.rawContractors.length
    );
  }

  get shouldBlockEditting(): boolean {
    return this.isEdittingLocked && !Boolean(this.activityInfoForModal?.isNew);
  }

  get customPlaceholderMessage(): string {
    if (this.numberOfAppliedFilters && !this.contractors.length) {
      return getEmptyFiltersResultMessage("contractors");
    }
    return getEmptyScheduleListMessage("contractors");
  }

  get propToWatch(): string {
    return JSON.stringify([
      this.selectedContractorsIds,
      this.additionalFilters,
    ]);
  }

  get showLinkToProject(): boolean {
    return Boolean(!this.activityInfoForModal?.isNew);
  }

  get options(): TimelineOptionsWithTemplates {
    return {
      start: this.passedStartDate,
      end: this.passedEndDate,
      autoResize: true,
      onAdd: function (item, callback) {
        item.className = "activityColor newItem";
        callback(item);
      },
    };
  }

  get isManager(): boolean {
    return this.currentUser.role === Roles.MANAGER;
  }

  get isAdmin(): boolean {
    return this.currentUser.role == Roles.ADMIN;
  }

  get startTime() {
    return this.queryStartTime;
  }

  get contractorGroups(): CustomTimelineGroup[] {
    if (!this.contractors) {
      return [];
    }
    return this.contractors.map((contractor) => {
      const contractorUser = contractor.user;
      return {
        id: contractor.id,
        name: getFullName(
          contractorUser.given_name!,
          contractorUser.family_name!
        ),
        avatar: contractorUser.avatar || "",
      };
    });
  }

  get activitiesIds(): string[] {
    return this.contractors
      .map((contractor) => {
        return contractor.project_activities.map((activity) => activity.id);
      })
      .flat();
  }

  get contractorActivityItems() {
    if (!this.contractors) {
      return [];
    }
    const activities = this.contractors.map((contractor) => {
      const groupId = contractor.id;
      return contractor.project_activities.map((contractorActivity, index) => {
        let activityMaxEndDate: Date = null;
        let activityMinStartDate: Date = null;
        const startDate = contractorActivity.start_date ? new Date(contractorActivity.start_date) : null;
        const endDate = contractorActivity.end_date ? new Date(contractorActivity.end_date) : null;

        if (activityMaxEndDate === null) {
          activityMaxEndDate = new Date(contractorActivity.end_date)
          activityMinStartDate = new Date(contractorActivity.start_date)
        }
        else if (startDate && endDate) {
          activityMaxEndDate = activityMaxEndDate.getTime() - endDate.getTime() > 0 ? activityMaxEndDate : endDate;
          activityMinStartDate = activityMinStartDate.getTime() - startDate.getTime() > 0 ? startDate : activityMinStartDate;
        }
        const [adjustedEnd, adjustedStart] = adjustDates(contractorActivity.end_date, contractorActivity.start_date, contractorActivity.project.start_date);
        let eventObj: any = {
          id: contractorActivity.id,
          resourceId: contractorActivity.id,
          index: index + 1,
          name: moment(contractorActivity.start_date).format("DD/MM/YYYY"),
          className: "activityColor",
          content: moment(contractorActivity.start_date).format("DD/MM/YYYY"),
          start: contractorActivity.start_time,
          end: contractorActivity.end_time,
          startDate: contractorActivity.start_time,
          endDate: contractorActivity.end_time,
          type: "activity",
          entityInfo: {
            activity: contractorActivity as ActivityDataForModal,
            project: contractorActivity.project as ProjectDataForModal,
            contractorId: contractor.id,
          },
        }

        return eventObj;
      });
    });

    return activities.flat();
  }

  get contractorProjectItems() {
    if (!this.contractors) {
      return [];
    }
    const projects = [];
    const uniqeSources = new Map();
    this.contractors.forEach((contractor) => {
      contractor.project_activities.forEach((activity) => {
        const project = activity.project;
        const projectStartDate = project.start_date;
        const projectEndDate = project.end_date;

        const uniqeSource = `${contractor.id}-${project.id}`;
        if (uniqeSources.has(uniqeSource)) {
          return;
        }
        uniqeSources.set(uniqeSource, true);

        projects.push({
          id: uniqeSource,
          type: "project",
          end: projectEndDate,
          color: hslColorFromName(contractor.user.given_name!),
          resourceId: uniqeSource,
          startDate: ensureTimezoneSuffix(projectStartDate),
          endDate: ensureTimezoneSuffix(projectEndDate),
          name: project.client_name,
          entityInfo: {
            project: project as ProjectDataForModal,
          },
        });
      });
    });
    return projects;
  }

  get contractorWithProjects(): any[] {
    if (!this.contractors) {
      return [];
    }

    //group contractors by project

    return this.contractors
      .map((contractor, i) => {
        const groupId = contractor.id;

        //group activities by project
        const projects = contractor.project_activities.filter(pa => pa.project != null).reduce(
          (acc, activity) => {
            const project = activity.project;
            const projectActivities = acc[project?.id] || [];
            projectActivities.push(activity);
            acc[project.id] = projectActivities;
            return acc;
          },
          {} as { [key: string]: ActivityDataForModal[] }
        );
        const color = EVENTS_COLORS[i % EVENTS_COLORS.length];
        return {
          id: contractor!.id,
          title: contractor.tag?.name || contractor.user?.profile?.job_title,
          email: contractor.user?.email,
          name: getFullName(
            contractor.user.given_name!,
            contractor.user.family_name!
          ),
          avatar: (contractor as unknown as any).profileImage,
          color: color,

          projects: Object.values(projects).map((projectActivities) => {
            const project = projectActivities[0].project;

            return {
              id: project.id,
              name: project.client_name,
              start_date: project.start_date,
              end_date: project.end_date,
              completed: project.completed,
              city: project.city,
              country: project.country,
              state: project.state,
              manager: project.manager,
              min_budget: project.min_budget,
              max_budget: project.max_budget,
              description: project.description,
              color: color,
              activities: projectActivities.map((activity) => {
                return {
                  id: activity.id,
                  group: groupId,
                  content: moment(activity.start_date).format('DD/MM/YYYY'),
                  className: "activityColor",
                  start_date: activity.start_date,
                  end_date: activity.end_date,
                  completed: activity.completed,
                  location: activity.location,
                  virtual: activity.virtual || false,
                  virtual_meeting_link: activity.virtual_meeting_link || null,
                  fallback_num: activity.fallback_num || null,
                  title: moment(activity.start_date).format('DD/MM/YYYY'),
                  note: activity.note,
                  budget: activity.budget,
                  contractor: activity.contractor,
                  confirmed_hours: activity.confirmed_hours,
                  color: color,
                  entityInfo: {
                    activity: activity as ActivityDataForModal,
                    project: project as ProjectDataForModal,
                    contractorId: contractor.id,
                  },
                };
              }),
            };
          }),
        };
      })
      .flat();
    // return activities.flat();
  }

  get activitiesWhere(): Project_Activity_Bool_Exp {
    // Add date filter if searchDate is set
    if (this.searchDate) {
      const formattedDate = moment(this.searchDate).format('DD/MM/YYYY');
      return {
        _or: [
          // Activity start date matches
          { start_date: { _gte: formattedDate, _lt: moment(formattedDate).add(1, 'day').format('DD/MM/YYYY') } },
          // Activity end date matches
          { end_date: { _gte: formattedDate, _lt: moment(formattedDate).add(1, 'day').format('DD/MM/YYYY') } },
          // Date is between activity start and end
          {
            _and: [
              { start_date: { _lte: formattedDate } },
              { end_date: { _gte: formattedDate } }
            ]
          }
        ],
        drafted: { _neq: true }
      };
    }

    // Default query without date filter
    return {
      _or: [
        {
          start_date: {
            _lte: this.queryEndTime,
            _gt: this.queryStartTime,
          },
        },
        {
          end_date: {
            _gte: this.queryStartTime,
            _lte: this.queryEndTime,
          },
        },
      ],
      drafted: { _neq: true }
    };
  }

  get where(): Contractor_Bool_Exp {
    const { countries, states, cities, titles, skills, minRate, maxRate, users } =
      this.additionalFilters;

    const filterConditions: Contractor_Bool_Exp[] = [
      {
        _and: [{ approved: { _eq: true } }, { deleted: { _eq: false } }, { project_activities: {} }],
      },
    ];

    // Add resource name search filter
    if (this.searchName && this.searchName.trim()) {
      filterConditions.push({
        _or: [
          {
            user: {
              _or: [
                { given_name: { _ilike: `%${this.searchName}%` } },
                { family_name: { _ilike: `%${this.searchName}%` } },
                { email: { _ilike: `%${this.searchName}%` } }
              ]
            }
          },
          {
            tag: {
              name: { _ilike: `%${this.searchName}%` }
            }
          }
        ]
      });
    }

    // Add location/address search filter
    if (this.searchLocation && this.searchLocation.trim()) {
      filterConditions.push({
        _or: [
          // Search in project location fields
          {
            project_activities: {
              project: {
                _or: [
                  { address: { _ilike: `%${this.searchLocation}%` } },
                  { location: { _ilike: `%${this.searchLocation}%` } },
                  { city: { _ilike: `%${this.searchLocation}%` } },
                  { state: { _ilike: `%${this.searchLocation}%` } },
                  { country: { _ilike: `%${this.searchLocation}%` } }
                ]
              }
            }
          },
          // Search in user profile location fields that exist in the schema
          {
            user: {
              profile: {
                _or: [
                  { city: { _ilike: `%${this.searchLocation}%` } },
                  { state: { _ilike: `%${this.searchLocation}%` } },
                  { country: { _ilike: `%${this.searchLocation}%` } }
                ]
              }
            }
          }
        ]
      });
    }

    if (this.isManager) {
      filterConditions.push({
        managers: {
          approved: {
            _eq: true,
          },
          manager: {
            user_id: {
              _eq: this.currentUser.id,
            },
          },
        },
      });
    }

    if (this.selectedContractorsIds?.length) {
      filterConditions.push({
        id: {
          _in: this.selectedContractorsIds,
        },
      });
    }

    if (countries.length) {
      filterConditions.push({
        user: {
          profile: {
            country: { _in: countries },
          },
        },
      });
    }

    if (users.length) {
      filterConditions.push({
        id: { _in: users }
      });
    }

    if (states.length) {
      filterConditions.push({
        user: {
          profile: {
            state: { _in: states },
          },
        },
      });
    }

    if (cities.length) {
      filterConditions.push({
        user: {
          profile: {
            city: { _in: cities },
          },
        },
      });
    }

    if (titles.length) {
      filterConditions.push({
        tag: {
          name: { _in: titles },
        },
      });
    }

    if (skills.length) {
      filterConditions.push({
        project_activities: {
          project: {
            project_skills: {
              skill
                : {
                id: { _in: skills }
              },
            },
          },
        },
      });
    }

    if (minRate || maxRate) {
      const minConditions: User_Profile_Bool_Exp[] = [];
      const maxConditions: User_Profile_Bool_Exp[] = [];

      if (minRate) {
        minConditions.push({ min_rate: { _gte: minRate } });
        maxConditions.push({ max_rate: { _gte: minRate } });
      }
      if (maxRate) {
        minConditions.push({ min_rate: { _lte: maxRate } });
        maxConditions.push({ max_rate: { _lte: maxRate } });
      }

      if (minConditions.length && maxConditions.length) {
        filterConditions.push({
          user: {
            profile: {
              _or: [{ _and: minConditions }, { _and: maxConditions }],
            },
          },
        });
      }
    }

    return { _and: filterConditions };
  }

  getOverlappingActivities(item: ContractorViewActivity): string[] {
    if (!item.entityInfo) {
      return [];
    }
    const contractorId = item.entityInfo.contractorId || "";
    const contractorToCheck: Contractor = this.contractors.filter(
      (contractor) => contractor.id === contractorId
    )[0];
    const contractorActivities: Project_Activity[] =
      contractorToCheck?.project_activities || [];

    const overlappingActivities = contractorActivities.filter((activity) => {
      return contractorActivities.some((activityOne) => {
        if (activityOne.id == activity.id) {
          return false;
        }
        if (
          activityOne.start_date >= activity.start_date &&
          activityOne.start_date <= activity.end_date
        ) {
          return true;
        }
        if (
          activity.start_date >= activityOne.start_date &&
          activity.start_date <= activityOne.end_date
        ) {
          return true;
        }
        return false;
      });
    });
    return overlappingActivities.map((activity) => activity.id);
  }

  async rangeChanged(e: RangeChangedPayload): Promise<void> {
    this.$emit("rangechanged", e);
    const fetchMoreDates = getFetchMoreStartDates(
      this.queryStartTime,
      this.queryEndTime,
      e
    );
    if (!fetchMoreDates) {
      return;
    }
    const { queryStartTime, queryEndTime, whereDatesConditions } =
      fetchMoreDates;
    this.queryStartTime = queryStartTime;
    this.queryEndTime = queryEndTime;

    try {
      await this.$apollo.queries.raw_Contractors.fetchMore({
        variables: {
          where: this.where,
          activitiesWhere: { _or: whereDatesConditions, project: {} },
          aggregateWhere: { deleted: { _eq: false } }
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          const updatedContractors: Contractor[] = fetchMoreResult.contractor;
          const res = {
            contractor: previousResult.contractor.map(
              (contractor: Contractor) => {
                const correspondingContractor: Contractor | undefined =
                  updatedContractors.find((updatedContractor) => {
                    return updatedContractor.id === contractor.id;
                  });
                if (correspondingContractor) {
                  const newActivities =
                    correspondingContractor.project_activities.filter(
                      (activity) => {
                        return !this.activitiesIds.includes(activity.id);
                      }
                    );
                  return {
                    ...contractor,
                    project_activities: Array.from(
                      new Set([
                        ...contractor.project_activities,
                        ...newActivities,
                      ])
                    ),
                    __typename: "contractor",
                  };
                } else {
                  return contractor;
                }
              }
            ),
          };
          return res;
        },
      });
    } catch (e) {
      this.$eventBus.emit(
        NOTIFICATION,
        Message.errorMessage(defaultErrorNotificationMessage)
      );
    }
  }

  handleCloseModal(): void {
    if (this.modalShown == false) {
      return;
    }
    if (this.newlyCreatedItemId) {
      this.clearNewlyCreatedItemId();
      // this.setCurrentlyFetchedItems();
    }
    // this.clearContractorIdForModal();
    this.closeModal();
  }

  async handleCollectionUpdated(): Promise<void> {
    this.clearNewlyCreatedItemId();
    await this.refetchCollection();
    this.closeModal();
  }

  async refetchCollection(): Promise<void> {
    await this.$apollo.queries.raw_Contractors.refetch({
      where: this.where,
      activitiesWhere: { ...this.activitiesWhere, project: {} },
      aggregateWhere: this.aggregateWhere
    });
  }

  clearNewlyCreatedItemId(): void {
    this.newlyCreatedItemId = undefined;
  }

  closeModal(): void {
    this.modalShown = false;
    this.isEdittingLocked = true;
  }

  handleIconClose(): void {
    this.$emit("close");
  }

  handleCreateBtnClicked(activity) {
    this.activityInfoForModal = null;
    this.projectInfoForModal = activity.entityInfo.project;
    this.contractorIdForModal = activity.group;
    this.modalType = "activity";
    this.modalShown = true;
  }

  handleActivityEventClicked({ activity, project }: any) {
    this.activityInfoForModal = activity;
    this.projectInfoForModal = project;
    this.modalShown = true;
    this.modalType = "activity";
  }

  handleProjectEventClicked({ activity, project }: any) {
    this.activityInfoForModal = activity;
    this.projectInfoForModal = project;
    this.modalShown = true;
    this.modalType = "project";
  }

  handleApplyFilters(filters: ContractorViewFiltersToWatch) {
    this.additionalFilters.countries = filters.countries;
    this.additionalFilters.states = filters.states;
    this.additionalFilters.cities = filters.cities;
    this.additionalFilters.titles = filters.titles;
    this.additionalFilters.skills = filters.skills;
    this.additionalFilters.minRate = filters.minRate;
    this.additionalFilters.maxRate = filters.maxRate;
    this.additionalFilters.users = filters.users;
    this.passedStartDate = filters.rangeStart;
    this.passedEndDate = filters.rangeEnd;
    this.handleIconClose();
  }

  handleReset(filters?: ContractorViewFiltersToWatch): void {
    // Clear search fields
    this.searchName = "";
    this.searchLocation = "";
    this.searchDate = "";

    if (filters) {
      // This is the case when called from advanced filter component
      this.$emit("filtersReset");
      this.additionalFilters.countries = filters.countries;
      this.additionalFilters.states = filters.states;
      this.additionalFilters.cities = filters.cities;
      this.additionalFilters.titles = filters.titles;
      this.additionalFilters.skills = filters.skills;
      this.additionalFilters.minRate = filters.minRate;
      this.additionalFilters.maxRate = filters.maxRate;
      this.additionalFilters.users = filters.users;
      this.passedStartDate = filters.rangeStart;
      this.passedEndDate = filters.rangeEnd;
      this.handleIconClose();
    } else {
      // This is the case when reset button is clicked directly
      this.additionalFilters = cloneDeep(contractorViewFiltersTemplate);
      this.$emit("filtersReset", cloneDeep(contractorViewFiltersTemplate));
    }

    // Trigger refetch
    this.handleSearchFiltersChange();
  }

  handleAppliedFiltersNumberChanged(num: number) {
    this.numberOfAppliedFilters = num;
  }
}
